import React, { createContext, useCallback, useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// Hooks
import useNotify from '../notify';

// Services
import { Spinner } from '@arredo/reactjs-ui';
import cartService from '../../services/cartService';
import stockControlService from '../../services/stockControlService';

// Models
import { ShoppingCart as ShoppingCartDraft } from '../../models/ShoppingCart';
import { IsPaymentRefusedWithApprovedPayment, OrderStates } from '../../models/OrderEntity';
import { PaymentStates } from '../../models/PaymentStates';
import { utils } from '../../utils/utils';
import useUser from '../user/useUser';

export const ShoppingCartContext = createContext({
    sale: null,
    warehouses: [],
    update: () => {},
    setSale: () => {},
    setIgnoreDiscount: () => {}
});

export const CartProvider = ({ orderId, children }) => {
    const { branch } = useUser();
    const [warehouses, setWarehouses] = useState([]);
    const push = useNavigate();
    const { Notify, variants } = useNotify();
    const [sale, setSale] = useState(ShoppingCartDraft);
    const [loadingGet, setLoadingGet] = useState(false);
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [ignoreDiscount, setIgnoreDiscount] = useState([]);

    useEffect(() => {
        const GetWarehouses = async () => {
            const res = await stockControlService.GetWarehouses({ 
                active: true, 
                isExport: true
            })

            setWarehouses(res.values);
        }

        GetWarehouses();
    }, [])

    useEffect(() => {
        const getFromServer = async () => {
            setLoadingGet(true);
            try {
                const sale = await cartService.Get(orderId);
                setSale(sale);
                setLoadingGet(false);
                
                if( sale.orderState === OrderStates.PaymentInitiated ) {
                    const payments = sale.payments.filter(p => p.state === PaymentStates.Initialized);
                    const payment = payments
                        .find(p => p.state === PaymentStates.Initialized)
    
                    if (payment) {
                        if (payments.length === 1 && payment.amount < sale.amount) {
                            const paidAmount = sale.payments
                                .filter(p => p.state === PaymentStates.Approved)
                                .reduce((acum, curr) => acum + curr.amount, 0);

                            if (paidAmount === 0) {
                                window.location.href = payment.cancelationUrl;
                                return
                            }
                        }
                        window.location.href = payment.paymentUrl;
                    }
                } else if (IsPaymentRefusedWithApprovedPayment({ state: sale.orderState, payments: sale.payments })) {
                    // Se llega acá al cancelar en payment-web un pago pendiente cuando otro ya fue aprobado
                    // (la CancelationUrl del pago apunta al carrito). No se pueden rearmar los pagos con uno
                    // aprobado: la venta se resuelve cancelándola desde el detalle.
                    Notify('La orden tiene un pago aprobado y no puede editarse. Si desea anularla, cancele la venta.', variants.INFO);
                    push(`/orders/detail/${orderId}`);
                } else if( sale.orderState !== OrderStates.Initiated &&
                    sale.orderState !== OrderStates.PaymentRefused) {                
                    Notify(`Esta orden ya no puede ser editada. ${sale.orderState}`, variants.INFO);
                    push(`/orders/detail/${orderId}`);
                }
            } catch(err) {
                console.error(err);
                Notify(`Ocurrio un error al intentar obtener la orde:. ${orderId}`, variants.ERROR);
                push(`/orders`);
            }
        }

        if (orderId)
            getFromServer();
    }, [orderId, Notify, variants, push])

    const update = async (req) => {
        if( req.ignoredPromotionIds ) {
            setIgnoreDiscount(req.ignoredPromotionIds)
        } else{
            req.ignoredPromotionIds = ignoreDiscount
        }

        if (req.items) {
            const warehouseIds = req.items
                .filter(i => i.warehouseId !== branch.warehouseId)
                .map(i => i.warehouseId);
    
            const invalidWarehouses = warehouses
                .filter(w => warehouseIds.includes(w.id) && !w.shareStock)
                .map(w => w.name);
    
            if (invalidWarehouses.length > 0) {
                return Notify(`El stock de ${invalidWarehouses.join(', ')} no está disponible para utilizar`, variants.ERROR)
            }
        }
        setLoadingUpdate(true);
        return cartService
            .Update({ ...sale, ...req})
            .then(sale => setSale(state => ({
                ...state,
                ...sale,
                paymentMethodData: utils.mapPayment(sale),
                ignoredPromotionIds: req.ignoredPromotionIds || [],
                ignoredPromotions: req.ignoredPromotions || []
            })))
            .catch((err) => Notify(`${err.message}`, variants.ERROR))
            .finally(() => setLoadingUpdate(false));
    }   

    return (
        <ShoppingCartContext.Provider value={{ sale, warehouses, update, setSale, setIgnoreDiscount }}>
            { loadingGet && <Spinner lockScreen alpha={0.5} message='Obteniendo informacion de venta' /> }
            { loadingUpdate && <Spinner lockScreen alpha={0.5} message='Actualizando el carrito' /> }
            {children}
        </ShoppingCartContext.Provider>
    )
}

const useCart = () => {
    const { sale, warehouses = [], update, setSale, setIgnoreDiscount } = useContext(ShoppingCartContext);
    const toggleReturnMode = useCallback(() =>
        setSale(state => ({ ...state, returnMode: !state.returnMode }))
    , [setSale])
    const existProcessedPayment = sale?.payments?.some(p => p.state === PaymentStates.Approved);

    const clearShippingData = useCallback(() =>
        setSale(state => ({ ...state, shippingData: null }))
    , [setSale])

    return {
        sale,
        warehouses,
        update,
        toggleReturnMode,
        clearShippingData,
        setIgnoreDiscount,
        existProcessedPayment
    }
}

export default useCart;